import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

import useAddProductCompatibilityModal from "pages/ProductCompatibilities/components/AddProductCompatibilityModal/hooks/useAddProductCompatibilityModal";
import { REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL } from "config";

const AddProductCompatibilityModal = (props) => {
  const [serverProducts, setServerProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const {
    addProductCompatibilityModalIsOpen,
    hideAddProductCompatibilityModal,
  } = useAddProductCompatibilityModal();

  const submit = async (values, { resetForm }) => {
    const server = {
      ...values.baseProduct,
    };
    try {
      const response = await axios.post(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/base-products`,
        server
      );
      if (response.status === 200) {
        resetForm();
        hideAddProductCompatibilityModal();
        if (props.onAddedProductCompatibility) {
          props.onAddedProductCompatibility();
        }
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const ProductCompatibilitySchema = Yup.object().shape({
    baseProduct: Yup.object().shape({
      name: Yup.string().min(1, "Too Short!").required("Required"),
      sku: Yup.string().min(1, "Too Short!"),
      bcId: Yup.string().min(1, "Too Short!").required("Required"),
    }),
  });

  const formik = useFormik({
    initialValues: {
      baseProduct: { name: "", sku: "", bcId: "" },
    },
    validateOnMount: true,
    validationSchema: ProductCompatibilitySchema,
    onSubmit: submit,
  });

  const getServerProducts = useCallback(async () => {
    try {
      const categoriesToReturn = ["Servers", "Storage", "Switches"];
      const categoryIdsToReturn = categories
        .filter((category) => {
          return categoriesToReturn.indexOf(category.name) > -1;
        })
        .map((category) => {
          return category.id;
        });
      if (categoryIdsToReturn.length === 0) {
        return;
      }
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/products?categories:in=${categoryIdsToReturn}`
      );
      if (response.status === 200) {
        let products = response.data.products;
        if (products.length === 0) {
          return; // No need to update state as serverProducts are already empty;
        }
        products = products.filter((product) => {
          const alreadyAdded = props.baseProducts.find(
            (baseProduct) => baseProduct.id === product.id
          );
          if (alreadyAdded) {
            return false;
          } else {
            return true;
          }
        });
        setServerProducts(products);
        // Setting first server as selected on select drop-down.
        const server = products[0];
        if (server) {
          formik.setFieldValue("baseProduct", {
            name: server.name,
            sku: server.sku,
            bcId: server.id,
          });
        }
      }
    } catch (error) {
      console.log("error: ", error);
    }
  }, [formik, categories, props.baseProducts]);

  const getCategories = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/categories`
      );
      if (response.status === 200) {
        const categories = response.data.categories;
        setCategories(categories);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  useEffect(() => {
    getCategories(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (addProductCompatibilityModalIsOpen && serverProducts.length === 0) {
      getServerProducts();
    }
  }, [addProductCompatibilityModalIsOpen, serverProducts, getServerProducts]);

  useEffect(() => {
    if (!addProductCompatibilityModalIsOpen) {
      setServerProducts([]);
    }
  }, [addProductCompatibilityModalIsOpen]);

  const { baseProduct } = formik.values;

  return (
    <Modal
      show={addProductCompatibilityModalIsOpen}
      onHide={hideAddProductCompatibilityModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Product Compatibility</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group controlId="baseProduct" size="lg">
            <Form.Label>Select Base Product:</Form.Label>
            <Form.Control
              as="select"
              name="baseProduct"
              value={baseProduct.bcId}
              onChange={(e) => {
                const server = serverProducts.find(
                  (serverProduct) => serverProduct.id === Number(e.target.value)
                );
                formik.setFieldValue("baseProduct", {
                  name: server.name,
                  sku: server.sku,
                  bcId: server.id,
                });
              }}
            >
              {serverProducts.map((serverProduct) => (
                <option key={serverProduct.id} value={serverProduct.id}>
                  SKU: {serverProduct.sku} {serverProduct.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Button
            block
            size="lg"
            type="submit"
            style={{
              marginBottom: "40px",
            }}
            disabled={Object.keys(formik.errors).length !== 0}
          >
            Add
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddProductCompatibilityModal;
